import type { RejectionReason } from '../../types/api';
import { MAX_REGENERATIONS, MAX_REVISIONS } from './reviewLimits';

/**
 * The vocabulary of "I don't like this", written down once.
 *
 * There are three ways back into the pipeline and they cost wildly different
 * amounts, so the reviewer is asked the one question they can actually answer —
 * *what* is wrong — and this table turns that into the call. The API deliberately
 * does not reroute anything (`/reject` is a real rejection, `/revise` a rewrite,
 * `/redesign` a re-plan of the pictures); the routing is here, in front of the
 * person who has to live with the cost.
 */

/** Which endpoint a complaint goes to. */
export type SendBackPath = 'revise' | 'redesign' | 'regenerate';

export interface SendBackReason {
  value: RejectionReason;
  /** Chip label. Plain words about the draft, never about the machinery. */
  label: string;
  /** What this complaint buys. */
  path: SendBackPath;
  /** Step 2 only — at copy selection there is nothing rendered to complain about. */
  visualOnly?: boolean;
}

/**
 * The chips, in the order they are offered: the three cheap wording complaints
 * first, then the angle (which is a new run however it is phrased), then the
 * pictures, then the catch-all.
 */
export const SEND_BACK_REASONS: readonly SendBackReason[] = [
  { value: 'copy_tone', label: 'Tone / voice', path: 'revise' },
  { value: 'copy_facts', label: 'A fact is wrong', path: 'revise' },
  { value: 'copy_length', label: 'Too long / short', path: 'revise' },
  { value: 'copy_angle', label: 'Wrong angle', path: 'regenerate' },
  { value: 'visual', label: 'Slides look wrong', path: 'redesign', visualOnly: true },
  { value: 'other', label: 'Something else', path: 'regenerate' },
];

/**
 * Narrows a chip's value back to the vocabulary. The options come from the
 * table above, so this never fails in practice — it is here so nothing on this
 * path is cast.
 */
export function isRejectionReason(value: string): value is RejectionReason {
  return SEND_BACK_REASONS.some((entry) => entry.value === value);
}

/** The chip a reason belongs to, or null for a reason we have no chip for. */
export function sendBackReason(reason: RejectionReason): SendBackReason | null {
  return SEND_BACK_REASONS.find((entry) => entry.value === reason) ?? null;
}

/** Where a complaint goes by default. The reviewer may still override it. */
export function pathForReason(reason: RejectionReason): SendBackPath {
  return sendBackReason(reason)?.path ?? 'regenerate';
}

/**
 * The chip's own words, for anywhere a recorded reason is read back — the
 * lineage panel. An attempt sent back before the chips existed carries null.
 */
export function reasonLabel(reason: RejectionReason | null): string {
  if (reason === null) return 'No reason recorded';
  return sendBackReason(reason)?.label ?? 'Something else';
}

/**
 * The chip's label on the screen it is drawn on. Only the visual chip moves: a
 * generated single is one square, and the rest of the product never calls it a
 * slide.
 */
export function chipLabel(reason: SendBackReason, subject: 'carousel' | 'image'): string {
  if (reason.value === 'visual' && subject === 'image') return 'The image looks wrong';
  return reason.label;
}

/** Button wording for a path, following what is on screen at step 2. */
export function pathAction(path: SendBackPath, subject: 'carousel' | 'image'): string {
  switch (path) {
    case 'revise':
      return 'Revise caption';
    case 'redesign':
      return subject === 'carousel' ? 'Redesign slides' : 'Redesign the image';
    case 'regenerate':
      return 'Regenerate';
  }
}

/**
 * One line about what the chosen path costs, before it is spent.
 *
 * The number is said out loud rather than left to a 409 to explain, because the
 * whole point of the chips is that the reviewer can see they are picking the
 * cheap option.
 */
export function pathCost(
  path: SendBackPath,
  /** What the lineage has already spent of the budget this path draws on. */
  used: number,
  step: 'copy' | 'visual',
): string {
  switch (path) {
    case 'revise':
      return `One quick edit. Uses 1 of your ${MAX_REVISIONS} revisions (${used} used).`;
    case 'redesign':
      return `Keeps the caption and the art. Uses 1 of your ${MAX_REVISIONS} revisions (${used} used).`;
    case 'regenerate':
      return step === 'copy'
        ? `Writes two new versions from scratch — it rejects both versions, not just this one. Starts try ${Math.min(used + 1, MAX_REGENERATIONS)} of ${MAX_REGENERATIONS}.`
        : `Writes two new versions from scratch. Starts try ${Math.min(used + 1, MAX_REGENERATIONS)} of ${MAX_REGENERATIONS}.`;
  }
}
